<template>
  <div>

    <v-container fluid>
      <v-row dense>
        <v-col>
          <v-card-title>General Metrics</v-card-title>
          <v-simple-table dense>
            <template v-slot:default>
              <tbody>
              <tr><td>Registered Users</td><td>{{ statistics.userRegistrations }}</td></tr>
              <tr><td>Courses</td><td>{{ statistics.coursesTotal }}</td></tr>
              <tr><td>Challenges</td><td>{{ statistics.challengesTotal }}</td></tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
        <v-col>
          <v-card-title>Versions</v-card-title>
          <v-simple-table dense>
            <template v-slot:default>
              <tbody>
              <tr><td>eSail Game</td><td>{{ latestEsailVersion }}</td></tr>
              <tr><td>Server API</td><td>{{ apiVersion }}</td></tr>
              <tr><td>Web UI</td><td>{{ appVersion }}</td></tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>
    </v-container>

    <v-card-title>eSail Versions</v-card-title>
    <bar-chart :chart-data="eSailVersionsChartData" :options="defaultChartOptions"/>

    <v-card-title>General Usage</v-card-title>
    <v-container fluid>
      <v-row dense>
        <v-col align="center">Users having used Multiplayer Mode<pie-chart :chart-data="usersUsingMultiplayerChartData" :options="defaultChartOptions"/></v-col>
        <v-col align="center">Users receiving Notifications<pie-chart :chart-data="usersReceivingNotificationsChartData" :options="defaultChartOptions"/></v-col>
        <v-col align="center">Users having used WebUI<pie-chart :chart-data="usersUsingWebUiChartData" :options="defaultChartOptions"/></v-col>
        <v-col align="center"><bar-chart style="height: 250px;" :chart-data="mauChartData" :options="defaultChartOptions"/></v-col>
      </v-row>
    </v-container>

    <v-card-title>Challenge Usage (based on leaderboard analysis)</v-card-title>
    <v-container fluid>
      <v-row dense>
        <v-col align="center"><pie-chart :chart-data="usersCompletingChallengesChartData" :options="defaultChartOptions"/></v-col>
        <v-col align="center"><pie-chart :chart-data="challengeTypeCompletions" :options="defaultChartOptions"/></v-col>
      </v-row>
    </v-container>

    <v-card-title>Created Challenge & Course Types</v-card-title>
    <v-container fluid>
      <v-row dense>
        <v-col align="center"><pie-chart :chart-data="challengePublishTypesChartData" :options="defaultChartOptions"/></v-col>
        <v-col align="center"><pie-chart :chart-data="challengeTypesChartData" :options="defaultChartOptions"/></v-col>
        <v-col align="center"><pie-chart :chart-data="courseTypesChartData" :options="defaultChartOptions"/></v-col>
      </v-row>
    </v-container>

    <v-card-title>User Origins</v-card-title>
    <bar-chart style="height: 250px;" :chart-data="userCountriesChartData" :options="defaultChartOptions"/>

  </div>
</template>

<script>
import {eSailServerPublic} from "@/plugins/axios";
import _ from "lodash";
import {version} from '../../../package.json'
import BarChart from "@/components/charts/BarChart";
import PieChart from "@/components/charts/PieChart";
import 'chartjs-plugin-colorschemes';

export default {
  name: "GeneralStatistics",
  components: {PieChart, BarChart},
  data: () => ({
    statistics: {
      userRegistrations: "",
      userCountries: [],
      coursesTotal: 0,
      challengesTotal: 0,
      challengeTypes: [],
      challengePublishTypes: [],
      courseTypes: [],
    },
    apiVersion: null,
    appVersion: version,
    latestEsailVersion: null,
    userCountriesChartData: {},
    usersCompletingChallengesChartData: {},
    challengeTypeCompletions: {},
    courseTypesChartData: {},
    challengeTypesChartData: {},
    challengePublishTypesChartData: {},
    eSailVersionsChartData: {},
    usersUsingMultiplayerChartData: {},
    usersUsingWebUiChartData: {},
    usersReceivingNotificationsChartData: {},
    mauChartData: {},
    defaultChartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        colorschemes: {
          scheme: 'tableau.Tableau10'
        }
      }
    }
  }),
  mounted() {
    eSailServerPublic.get('info/version')
      .then(response => (
          this.apiVersion = response.data.version
      ))
    eSailServerPublic.get('admin/statistics/general?period=TOTAL', { headers: { Authorization: `Bearer ` + this.$auth.token() }})
        .then(response => {
          const numUserRegistrations = _.sumBy(response.data.userRegistrations, 'count')
          let userRegistrations = numUserRegistrations
          const inactiveUsers = _.find(response.data.userRegistrations, { name: 'false' })
          if (inactiveUsers) {
            userRegistrations += " (of which " + inactiveUsers.count + " pending activations)"
          }
          const activeUsers = numUserRegistrations - inactiveUsers.count
          this.statistics = response.data
          this.statistics.userRegistrations = userRegistrations

          this.mauChartData = {
            labels: _.map(this.statistics.uniqueUserLeaderboardsPerMonth, item => {
              return item.year + "-" + item.month
              }),
            datasets: [{
              type: 'bar',
              order: 2,
              label: "MAU count",
              data: _.map(this.statistics.uniqueUserLeaderboardsPerMonth, 'count')
            },
            {
              type: 'line',
              order: 1,
              label: "MAU in Percent",
              data: _.map(this.statistics.uniqueUserLeaderboardsPerMonth, item => {
                return Math.round(item.count * 100 / activeUsers)
              })
            }]
          }

          this.usersUsingMultiplayerChartData = {
            labels: [ 'true', 'false' ],
            datasets: [{
              data: [ activeUsers - this.statistics.usersNeverRunMultiplayer, this.statistics.usersNeverRunMultiplayer ]
            }]
          }
          this.usersUsingWebUiChartData = {
            labels: [ 'true', 'false' ],
            datasets: [{
              data: [ this.statistics.usersAccessingWebUi, activeUsers - this.statistics.usersAccessingWebUi ]
            }]
          }
          this.usersReceivingNotificationsChartData = {
            labels: [ 'true', 'false' ],
            datasets: [{
              data: [ this.statistics.usersReceivingNotifications, activeUsers - this.statistics.usersReceivingNotifications ]
            }]
          }

          this.latestEsailVersion = _.maxBy(this.statistics.esailVersions, item => {
            return item.name.replace(/\./g, '');
          }).name
          let versionColors = []
          _.each(this.statistics.esailVersions, item => {
            versionColors.push(item.name === this.latestEsailVersion ? 'orange' : undefined)
          })

          this.eSailVersionsChartData = {
            labels: _.map(this.statistics.esailVersions, 'name'),
            grouped: false,
            datasets: [{
              label: 'eSail Versions (latest in orange)',
              data: _.map(this.statistics.esailVersions, 'count'),
              backgroundColor: versionColors
            }]
          }

          this.userCountriesChartData = {
            labels: _.map(this.statistics.userCountries, 'name'),
            datasets: [{
              label: "Countries of origin",
              data: _.map(this.statistics.userCountries, 'count')
            }]
          }

          this.challengeTypeCompletions = {
            labels: _.map(this.statistics.challengeTypeCompletions, 'name'),
            datasets: [{
              data: _.map(this.statistics.challengeTypeCompletions, 'count')
            }]
          }
          const numUsersCompletingChallenges = this.statistics.usersChallengeCompletions.length
          this.usersCompletingChallengesChartData = {
            labels: [ 'users who completed challenges', 'users who never completed at least one challenge' ],
            datasets: [{
              data: [ numUsersCompletingChallenges, numUserRegistrations - inactiveUsers.count - numUsersCompletingChallenges ]
            }]
          }

          this.challengeTypesChartData = {
            labels: _.map(this.statistics.challengeTypes, 'name'),
            datasets: [{
              data: _.map(this.statistics.challengeTypes, 'count')
            }]
          }
          this.challengePublishTypesChartData = {
            labels: _.map(this.statistics.challengePublishTypes, 'name'),
            datasets: [{
              data: _.map(this.statistics.challengePublishTypes, 'count')
            }]
          }
          this.courseTypesChartData = {
            labels: _.map(this.statistics.courseTypes, item => { return item.name + " (Course)" } ),
            datasets: [{
              data: _.map(this.statistics.challengePublishTypes, 'count')
            }]
          }
        })
  }
}
</script>

<style scoped>

</style>